<script lang="ts">
	import { goto } from '$app/navigation';
	import { Button } from '$lib/components/ui/button/index.js';
	import { Input } from '$lib/components/ui/input/index.js';
	import { Label } from '$lib/components/ui/label/index.js';
	import { Separator } from '$lib/components/ui/separator/index.js';
	import * as m from '$lib/paraglide/messages.js';
	import { localizeHref } from '@/paraglide/runtime';
	import {
		Collections,
		type UsersResponse,
		UsersStatusOptions
	} from '@/pocketbase/pocketbase-types';
	import { pb } from '@/utils/ApiClient';
	import { Eye, EyeOff, ShipWheel } from '@lucide/svelte';
	import CheckCircle2Icon from '@lucide/svelte/icons/check-circle-2';
	import CircleX from '@lucide/svelte/icons/circle-x';
	import { ClientResponseError, type RecordAuthResponse } from 'pocketbase';
	import posthog from 'posthog-js';
	import { toast } from 'svelte-sonner';

	const id = $props.id();

	let email: string = $state('');
	let password: string = $state('');
	let loading: boolean = $state(false);
	let showPassword = $state(false);
	let error: ClientResponseError | null = $state(null);
	let needsEmailVerification: boolean = $state(false);
	let sendingVerification: boolean = $state(false);
	let resendCooldown: number = $state(0);
	let isAccountPending: boolean = $state(false);

	let fieldErrors = $derived(
		error ? (error as any).response?.data || (error as any).originalError?.data?.data || {} : {}
	);

	async function login() {
		loading = true;
		error = null;
		needsEmailVerification = false;
		isAccountPending = false;

		try {
			const userResponse = await authenticateUser(email, password);

			if (!userResponse.record.verified) {
				needsEmailVerification = true;
				toast.error('Por favor verifica tu correo electrónico antes de iniciar sesión', {
					icon: CircleX
				});
				return;
			}

			const isApproved = checkUserAccountStatus(userResponse.record);
			if (!isApproved) {
				isAccountPending = true;
				toast.error('Tu cuenta aún está pendiente de aprobación por nuestro equipo', {
					icon: CircleX
				});
				return;
			}

			toast.success('¡Inicio de sesión exitoso!', {
				icon: CheckCircle2Icon
			});

			posthog.identify(email, {
				email: email
			});
			posthog.capture('user_logged_in', { email: email });
			goto(localizeHref('/cuenta'));
		} catch (err) {
			error =
				err instanceof ClientResponseError ? err : new ClientResponseError('Authentication failed');

			if (
				error.status === 403 &&
				error.message?.includes("doesn't satisfy the collection requirements")
			) {
				needsEmailVerification = true;
				toast.error('Por favor verifica tu correo electrónico antes de iniciar sesión', {
					icon: CircleX
				});
			} else {
				toast.error('Error al iniciar sesión', {
					icon: CircleX
				});
			}
		} finally {
			loading = false;
		}
	}

	async function authenticateUser(
		email: string,
		password: string
	): Promise<RecordAuthResponse<UsersResponse>> {
		return pb
			.collection(Collections.Users)
			.authWithPassword<UsersResponse>(email.toLowerCase(), password);
	}

	function checkUserAccountStatus(user: UsersResponse): boolean {
		return user.status === UsersStatusOptions.active;
	}

	async function resendVerification() {
		sendingVerification = true;
		try {
			await pb.collection('users').requestVerification(email);
			toast.success('¡Correo de verificación enviado!', {
				icon: CheckCircle2Icon
			});

			resendCooldown = 180;
			const timer = setInterval(() => {
				resendCooldown--;
				if (resendCooldown <= 0) {
					clearInterval(timer);
				}
			}, 1000);
		} catch (err) {
			toast.error('Error al enviar el correo de verificación', {
				icon: CircleX
			});
		} finally {
			sendingVerification = false;
		}
	}
	async function signInWithGoogle() {
		try {
			const authData = await pb.collection('users').authWithOAuth2({ provider: 'google' });
			console.log('authData', authData);
			posthog.identify(authData.record.email, {
				email: authData.record.email,
				id: authData.record.id
			});
			posthog.capture('user_logged_in', { email: authData.record.email });
			toast.success('¡Inicio de sesión exitoso!', {
				icon: CheckCircle2Icon
			});
			goto(localizeHref('/cuenta'));
		} catch (error) {
			console.error(error);
			toast.error('Error al iniciar sesión con Google', {
				icon: CircleX
			});
		} finally {
			loading = false;
		}
	}
	async function signInWithApple() {
		try {
			loading = true;
			const authData = await pb.collection('users').authWithOAuth2({ provider: 'apple' });
			console.log('authData', authData);

			// Verificar si el usuario está autenticado
			if (pb.authStore.isValid) {
				// Usar setTimeout para asegurar que la redirección ocurra después de que el estado de autenticación se haya actualizado
				setTimeout(() => {
					posthog.identify(authData.record.email, {
						email: authData.record.email,
						id: authData.record.id
					});
					posthog.capture('user_logged_in', { email: authData.record.email });
					toast.success('¡Inicio de sesión exitoso!', {
						icon: CheckCircle2Icon
					});
					goto(localizeHref('/cuenta'));
				}, 100);
			} else {
				console.error('La autenticación no fue exitosa');
				toast.error('Autenticación fallida. Por favor, inténtalo de nuevo.', {
					icon: CircleX
				});
			}
		} catch (error) {
			console.error('Error durante la autenticación con Apple:', error);
			toast.error('Ocurrió un error al iniciar sesión con Apple. Por favor, inténtalo de nuevo.', {
				icon: CircleX
			});
		} finally {
			loading = false;
		}
	}
</script>

<svelte:head>
	<title>Zarpar | Iniciar Sesión</title>
	<meta
		name="description"
		content="Inicia sesión en Zarpar para acceder a tu cuenta, gestionar tus reservas de barcos, publicar anuncios y disfrutar de todas las funcionalidades náuticas."
	/>
</svelte:head>

<div class="mb-8 w-full md:mb-0 lg:grid lg:min-h-[700px]">
	<div class="flex items-center justify-center md:-mt-20">
		<div class="mx-auto mt-12 grid w-[350px] gap-4 md:mt-0">
			<div class="grid gap-2 text-center">
				<h1 class="text-3xl font-bold">{m.away_brave_flamingo_zip()}</h1>
				<p class="text-muted-foreground">{m.tasty_tense_leopard_nurture()}</p>
			</div>
			<button
				onclick={() => {
					signInWithApple();
				}}
				class="flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus-visible:ring-transparent"
			>
				<svg
					class="h-5 w-5"
					viewBox="0 0 24 24"
					fill="#000000"
					version="1.1"
					xmlns="http://www.w3.org/2000/svg"
					xmlns:xlink="http://www.w3.org/1999/xlink"
					xml:space="preserve"
				>
					<g>
						<g>
							<path
								d="M15.769,0c0.053,0,0.106,0,0.162,0c0.13,1.606-0.483,2.806-1.228,3.675c-0.731,0.863-1.732,1.7-3.351,1.573
					   c-0.108-1.583,0.506-2.694,1.25-3.561C13.292,0.879,14.557,0.16,15.769,0z"
							></path>
							<path
								d="M20.67,16.716c0,0.016,0,0.03,0,0.045c-0.455,1.378-1.104,2.559-1.896,3.655c-0.723,0.995-1.609,2.334-3.191,2.334
					   c-1.367,0-2.275-0.879-3.676-0.903c-1.482-0.024-2.297,0.735-3.652,0.926c-0.155,0-0.31,0-0.462,0
					   c-0.995-0.144-1.798-0.932-2.383-1.642c-1.725-2.098-3.058-4.808-3.306-8.276c0-0.34,0-0.679,0-1.019
					   c0.105-2.482,1.311-4.5,2.914-5.478c0.846-0.52,2.009-0.963,3.304-0.765c0.555,0.086,1.122,0.276,1.619,0.464
					   c0.471,0.181,1.06,0.502,1.618,0.485c0.378-0.011,0.754-0.208,1.135-0.347c1.116-0.403,2.21-0.865,3.652-0.648
					   c1.733,0.262,2.963,1.032,3.723,2.22c-1.466,0.933-2.625,2.339-2.427,4.74C17.818,14.688,19.086,15.964,20.67,16.716z"
							></path>
						</g>
					</g></svg
				>
				<span class="text-sm leading-6 font-semibold">Apple</span>
			</button>
			<button
				onclick={() => {
					signInWithGoogle();
				}}
				class="flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus-visible:ring-transparent"
			>
				<svg class="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
					<path
						d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
						fill="#EA4335"
					></path>
					<path
						d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
						fill="#4285F4"
					></path>
					<path
						d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
						fill="#FBBC05"
					></path>
					<path
						d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
						fill="#34A853"
					></path>
				</svg>
				<span class="text-sm leading-6 font-semibold">Google</span>
			</button>
			<Separator class="my-4" />
			<form
				onsubmit={(e) => {
					e.preventDefault();
					login();
				}}
				class="grid gap-4"
			>
				<div class="grid gap-3">
					<Label for="email-{id}">{m.mean_broad_rooster_peek()}</Label>
					<Input
						bind:value={email}
						id="email-{id}"
						type="email"
						placeholder="hola@ejemplo.com"
						required
						disabled={loading}
						autocomplete="email"
						class="text-base md:text-sm"
						aria-invalid={fieldErrors.email ? true : undefined}
						aria-describedby={fieldErrors.email ? 'email-error-{id}' : undefined}
					/>
					{#if fieldErrors.email?.message}
						<p id="email-error-{id}" class="text-destructive text-sm">
							{fieldErrors.email.message}
						</p>
					{:else if needsEmailVerification}
						<div class="space-y-2">
							<p id="email-error-{id}" class="text-destructive text-sm">
								Necesitas verificar tu correo electrónico antes de iniciar sesión. Por favor revisa
								tu bandeja de entrada.
							</p>
							<Button
								type="button"
								variant="outline"
								size="sm"
								class="w-full"
								onclick={resendVerification}
								disabled={sendingVerification || !email || resendCooldown > 0}
							>
								{#if sendingVerification}
									<ShipWheel class="mr-2 h-4 w-4 animate-spin" />
									Enviando...
								{:else if resendCooldown > 0}
									Reenviar en {resendCooldown}s
								{:else}
									Reenviar correo de verificación
								{/if}
							</Button>
						</div>
					{:else if isAccountPending}
						<p id="email-error-{id}" class="text-destructive text-sm">
							Tu cuenta aún está pendiente de aprobación por nuestro equipo.
						</p>
					{/if}
				</div>
				<div class="relative grid gap-3">
					<Label for="password-{id}">{m.livid_weary_turkey_cuddle()}</Label>
					<div class="relative">
						<Input
							bind:value={password}
							id="password-{id}"
							type={showPassword ? 'text' : 'password'}
							required
							disabled={loading}
							autocomplete="current-password"
							class="pr-10 text-base md:text-sm"
							aria-invalid={fieldErrors.password || (!fieldErrors.email && error)
								? true
								: undefined}
							aria-describedby={fieldErrors.password || (!fieldErrors.email && error)
								? 'password-error-{id}'
								: undefined}
						/>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							class="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
							onclick={() => (showPassword = !showPassword)}
							disabled={loading}
						>
							{#if showPassword}
								<EyeOff class="text-muted-foreground h-4 w-4" />
							{:else}
								<Eye class="text-muted-foreground h-4 w-4" />
							{/if}
							<span class="sr-only">{showPassword ? 'Hide' : 'Show'} password</span>
						</Button>
					</div>
					<a
						href={localizeHref('/recuperacion-credenciales')}
						class="absolute top-0 right-0 text-sm underline"
						>{m.silly_sharp_mink_compose()}
					</a>
					{#if fieldErrors.password?.message}
						<p id="password-error-{id}" class="text-destructive text-sm">
							{fieldErrors.password.message}
						</p>
					{:else if !fieldErrors.email && error && !needsEmailVerification && !isAccountPending}
						<p id="password-error-{id}" class="text-destructive text-sm">
							Email o contraseña incorrectos. Por favor, inténtalo de nuevo.
						</p>
					{/if}
				</div>
				<Button type="submit" class="w-full" disabled={loading}>
					{#if loading}
						<ShipWheel class="mr-2 animate-spin" /> {m.patient_knotty_horse_tap()}
					{:else}
						{m.away_brave_flamingo_zip()}
					{/if}
				</Button>
			</form>
			<div class="mt-4 text-center text-sm">
				{m.ideal_left_hyena_earn()}
				<a href={localizeHref('/registro')} class="underline"> {m.fair_away_moth_gaze()} </a>
			</div>
		</div>
	</div>
	<!-- TODO: consider addig image 
	<div class="bg-muted hidden lg:block">
		<enhanced:img
			src="/static/img/arctica1.jpg"
			alt="placeholder"
			class="h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
		></enhanced:img>
	</div>
	-->
</div>
